import { PROVIDERS, type Chat, type ChatSpace } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowUp, CalendarClock, Check, ChevronDown, Code2, FileText, FolderOpen, MessageSquareDashed, MoreHorizontal, Pin, PinOff, Plug, Plus, Search, SquarePen, Trash2, Workflow, X } from "lucide-react";
import { Menu } from "@/ui/Menu.tsx";
import { MemoryShareDialog } from "./MemoryShareDialog.tsx";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { accountsApi, modelsApi } from "@/api/accounts.ts";
import { chatsApi } from "@/api/chats.ts";
import { projectsApi } from "@/api/projects.ts";
import { spacesApi } from "@/api/spaces.ts";
import { SpaceBadge } from "./SpaceBadge.tsx";
import { ChatHeaderToolsContext, ProjectPage, projectQuery } from "@/features/projects/ProjectPage.tsx";
import { ChatRow } from "@/features/shell/ProjectsSidebar.tsx";
import { softKeyboardOpen } from "@/lib/keyboard.ts";
import { useDraft } from "@/lib/draft.ts";
import { useMcpShortcuts } from "./McpShortcuts.tsx";
import { TemplatesButton } from "./Templates.tsx";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";
import { Alert, Button, Input, Select, Spinner } from "@/ui/primitives.tsx";
import { ProviderIcon } from "@/ui/ProviderIcon.tsx";
import { cn } from "@/lib/cn.ts";
import { useApiError } from "@/ui/useApiError.ts";

/** The user's personal space (one hidden, unlimited container per user) behind the Chat section. */
export function usePersonalSpace() {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["personal-space"],
    queryFn: async () => {
      const p = await projectsApi.personal();
      qc.setQueryData(projectQuery(p.id).queryKey, p);
      return p;
    },
    staleTime: 60_000,
  });
}

/** Route layout for /chat: the space's chat screen with its tools and dock, like a project's chat. */
export function SpaceLayout() {
  const toMsg = useApiError();
  const space = usePersonalSpace();
  if (space.isError) return <div className="p-6"><Alert tone="danger">{toMsg(space.error)}</Alert></div>;
  if (!space.data) return <div className="grid h-full place-items-center"><Spinner /></div>;
  return <ProjectPage projectId={space.data.id} personal />;
}

const PICK_KEY = "codvero.space.pick.v2";
/** The providers ticked for new chats and the model for a one-provider chat (remembered on this device). */
function readPick(): { accountIds: string[] | null; model: string } {
  try {
    const v = JSON.parse(localStorage.getItem(PICK_KEY) ?? "null") as { accountIds?: string[]; model?: string } | null;
    return { accountIds: v?.accountIds ?? null, model: v?.model ?? "default" };
  } catch {
    return { accountIds: null, model: "default" };
  }
}

/** /chat: a new conversation, Claude-style: a greeting and one box in the middle of the page.
 *  /chat/temporary: the same for a temporary one (deleted with all its files once the user leaves it). */
export function SpaceHome({ temporary = false }: { temporary?: boolean }) {
  const { t } = useTranslation();
  const tools = useContext(ChatHeaderToolsContext);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t("space.morning") : hour < 18 ? t("space.afternoon") : t("space.evening");
  return (
    <div className="flex h-full flex-col">
      {/* the space's tools (browser, files, terminal, providers…) are at hand before the first message too */}
      <header className="flex justify-end px-3 py-2 md:px-4">{tools}</header>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-4 pb-16">
        <div className="w-full max-w-2xl space-y-6">
          {temporary ? (
            <div className="space-y-2 text-center">
              <h1 className="inline-flex items-center gap-2 text-2xl font-semibold sm:text-3xl"><MessageSquareDashed className="size-7" /> {t("temporary.title")}</h1>
              <p className="text-sm text-fg-muted">{t("temporary.hint")}</p>
            </div>
          ) : (
            <h1 className="text-center text-2xl font-semibold sm:text-3xl">{greeting}</h1>
          )}
          <NewChatComposer key={temporary ? "temporary" : "chat"} suggestions={!temporary} temporary={temporary} />
        </div>
      </div>
    </div>
  );
}

/**
 * The box that starts a conversation: the providers (one or several) are ticked before the first message. Every
 * conversation here is a providers conversation, so providers can be added, picked or pinned later, like a team.
 * `spaceId`: start it inside a space (its instructions and files go to every provider).
 */
export function NewChatComposer({ spaceId, suggestions = false, placeholder, compact = false, autoFocus = true, lines = 3, temporary = false }: { spaceId?: string; suggestions?: boolean; placeholder?: string; compact?: boolean; autoFocus?: boolean; lines?: number; temporary?: boolean }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const nav = useNavigate();
  const qc = useQueryClient();
  const space = usePersonalSpace();
  const p = useQuery({ ...projectQuery(space.data?.id ?? ""), enabled: !!space.data, refetchInterval: (q) => (q.state.data?.state === "running" ? false : 1500) }).data;
  const accounts = useQuery({ queryKey: ["accounts"], queryFn: accountsApi.list, staleTime: 60_000 });
  const connected = (accounts.data ?? []).filter((a) => a.status === "connected");
  const [pick, setPick] = useState(readPick);
  const fallback = connected.find((a) => a.provider === "claude") ?? connected[0];
  const chosen = connected.filter((a) => (pick.accountIds ? pick.accountIds.includes(a.id) : a.id === fallback?.id));
  const solo = chosen.length === 1 ? chosen[0]! : null;
  const providerId = solo?.provider ?? "claude";
  const catalog = useQuery({ queryKey: ["models", providerId, solo?.id ?? null], queryFn: () => modelsApi.catalog(providerId, solo?.id ?? null), enabled: !!solo, staleTime: 5 * 60_000 });
  const models = catalog.data?.models ?? PROVIDERS[providerId].models.map((m) => ({ id: m.id, label: m.label }));
  const model = models.some((m) => m.id === pick.model) ? pick.model : "default";
  const save = (next: { accountIds: string[] | null; model: string }) => {
    setPick(next);
    try { localStorage.setItem(PICK_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  };
  const toggle = (id: string) => {
    const ids = new Set(chosen.map((a) => a.id));
    if (ids.has(id)) { if (ids.size > 1) ids.delete(id); } else ids.add(id);
    const next = [...ids];
    save({ accountIds: next, model: next.length === 1 && next[0] === solo?.id ? model : "default" });
  };
  // kept on this device until sent: closing the page no longer loses what was typed
  // the box's own switch makes the next conversation a temporary one (the temporary page's box always is)
  const [tempOn, setTempOn] = useState(false);
  const temp = temporary || tempOn;
  // (a temporary conversation's words are not kept on the device)
  const [text, setText] = useDraft(temporary ? null : `new:${spaceId ?? "home"}`);
  const ref = useRef<HTMLTextAreaElement>(null);
  const ready = p?.state === "running";
  // files picked before the conversation exists: uploaded to it once it is made, then sent with the first message
  const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const addFiles = (list: FileList | File[]) => setFiles((v) => [...v, ...Array.from(list)]);
  // the one-line box (home on a phone) grows with what is typed, up to its max height
  useEffect(() => {
    const el = ref.current;
    if (!compact || !el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [text, compact]);
  const start = useMutation({
    mutationFn: async (prompt: string) => {
      const m = await chatsApi.createMulti(p!.id, chosen.map((a) => a.id), solo && model !== "default" ? model : undefined, spaceId, temp);
      const attachments = [];
      for (const f of files) attachments.push(await chatsApi.upload(m.chat.id, f));
      await chatsApi.send(m.chat.id, prompt, attachments);
      return m.chat;
    },
    onSuccess: (chat) => {
      setText(""); // sent: the draft is done
      setFiles([]);
      void qc.invalidateQueries({ queryKey: ["chats", chat.projectId, "list"] });
      void qc.invalidateQueries({ queryKey: ["chat-spaces"] });
      qc.setQueryData(["chat-kind", chat.id], chat);
      void nav({ to: "/chat/$chatId", params: { chatId: chat.id } });
    },
  });
  const canSend = chosen.length > 0;
  const submit = () => { const v = text.trim(); if (v && ready && canSend && !start.isPending) start.mutate(v); };
  // "/" lists the MCP connections; the chosen one's shortcuts sit above the box
  const mcp = useMcpShortcuts(text, setText, () => ref.current?.focus());
  return (
    <div className={compact ? "space-y-2" : "space-y-4"}>
      <form className={cn("relative rounded-2xl border bg-surface shadow-sm focus-within:border-accent", temp ? "border-dashed border-fg-muted" : "border-border")} onSubmit={(e) => { e.preventDefault(); submit(); }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { if (e.dataTransfer.files.length) { e.preventDefault(); addFiles(e.dataTransfer.files); } }}>
        {mcp.menu}
        {mcp.bar}
        <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }} />
        {files.length > 0 && (
          <ul className={cn("flex flex-wrap gap-1.5", compact ? "px-2 pt-2" : "px-3 pt-3")}>
            {files.map((f, i) => (
              <li key={`${f.name}:${i}`} className="flex items-center gap-1.5 rounded-md border border-border bg-surface-2 py-1 ps-1.5 pe-1.5 text-xs">
                <FileText className="size-3.5 shrink-0 text-fg-muted" />
                <span className="max-w-40 truncate" dir="auto">{f.name}</span>
                <button type="button" className="text-fg-muted hover:text-danger" aria-label={t("common.remove")} onClick={() => setFiles((v) => v.filter((_, j) => j !== i))}><X className="size-3.5" /></button>
              </li>
            ))}
          </ul>
        )}
        <div className={compact ? "p-2" : "p-3"}>
        <textarea
          ref={ref}
          autoFocus={autoFocus}
          dir="auto"
          rows={compact ? 2 : lines}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={(e) => { const pasted = [...e.clipboardData.files]; if (pasted.length) { e.preventDefault(); addFiles(pasted); } }}
          onKeyDown={(e) => { if (mcp.onKeyDown(e)) return; if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && !softKeyboardOpen()) { e.preventDefault(); submit(); } }}
          placeholder={tempOn ? t("temporary.placeholder") : (placeholder ?? t("space.placeholder"))}
          className={cn("block w-full resize-none bg-transparent px-1 text-base outline-none placeholder:text-fg-muted", compact ? "max-h-[35vh] min-h-[3.5rem] py-1" : "max-h-[40vh]")}
        />
        <div className={cn("flex items-center gap-2", compact ? "mt-1" : "mt-2 flex-wrap")}>
          <button type="button" onClick={() => fileInput.current?.click()} aria-label={t("chat.attach")} title={t("chat.attachHint")} className="grid size-9 shrink-0 place-items-center rounded-md border border-border text-fg-muted hover:bg-surface-2 hover:text-fg md:size-8"><Plus className="size-4" /></button>
          {!temporary && (
            <button type="button" role="switch" aria-checked={tempOn} onClick={() => { setTempOn((v) => !v); ref.current?.focus(); }} aria-label={t("temporary.new")} title={tempOn ? t("temporary.on") : t("temporary.new")} className={cn("grid size-9 shrink-0 place-items-center rounded-md border md:size-8", tempOn ? "border-accent bg-accent-soft text-fg" : "border-border text-fg-muted hover:bg-surface-2 hover:text-fg")}><MessageSquareDashed className="size-4" /></button>
          )}
          <TemplatesButton spaceId={spaceId ?? null} onInsert={(x) => { setText((v) => (v.trim() ? `${v.replace(/\s*$/, "")}\n\n${x}` : x)); ref.current?.focus(); }} />
          {/* providers: tick one or several (each answers on its own) */}
          <div className={cn("flex min-w-0 items-center gap-1", compact ? "no-scrollbar flex-1 overflow-x-auto" : "flex-wrap")} role="group" aria-label={t("space.provider")}>
            {connected.map((a) => {
              const on = chosen.some((c) => c.id === a.id);
              return (
                compact ? (
                  // a phone's home box: the providers' marks only (the name is in the label and the tooltip)
                  <button key={a.id} type="button" role="checkbox" aria-checked={on} aria-label={`${PROVIDERS[a.provider].label} · ${a.label}`} title={`${PROVIDERS[a.provider].label} · ${a.label}`} onClick={() => toggle(a.id)} className={cn("grid size-9 shrink-0 place-items-center rounded-full border-2 transition-opacity", on ? "border-accent bg-accent-soft" : "border-transparent opacity-40 hover:opacity-70")}>
                    <ProviderIcon provider={a.provider} size="sm" />
                  </button>
                ) : (
                <button key={a.id} type="button" role="checkbox" aria-checked={on} title={`${PROVIDERS[a.provider].label} · ${a.label}`} onClick={() => toggle(a.id)} className={cn("flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs", on ? "border-accent bg-accent-soft text-fg" : "border-border text-fg-muted hover:text-fg")}>
                  <ProviderIcon provider={a.provider} size="xs" />
                  <span className="whitespace-nowrap">{PROVIDERS[a.provider].label}</span>
                  {on && chosen.length > 1 && <Check className="size-3 text-accent" />}
                </button>
                )
              );
            })}
          </div>
          {solo && !compact && (
            <Select aria-label={t("chat.chooseModel")} value={model} onChange={(e) => save({ accountIds: [solo.id], model: e.target.value })} className="h-8 w-auto max-w-[12rem] rounded-full px-2.5 text-xs">
              {models.map((m) => <option key={m.id} value={m.id}>{m.id === "default" ? t("chat.models.default") : m.label}</option>)}
            </Select>
          )}
          <span className={cn("text-xs text-fg-muted", !compact && "ms-auto")}>{ready ? "" : <span className="inline-flex items-center gap-2"><Spinner className="size-3" /> {t("space.preparing")}</span>}</span>
          <Button type="submit" className="size-9 rounded-full p-0" loading={start.isPending} disabled={!text.trim() || !ready || !canSend} aria-label={t("chat.send")}><ArrowUp className="size-4" /></Button>
        </div>
        </div>
      </form>
      {start.error && <Alert tone="danger">{toMsg(start.error)}</Alert>}
      {accounts.data && connected.length === 0 && <Alert tone="warn">{t("space.noProviders")}</Alert>}
      {tempOn && <p className="flex items-center justify-center gap-1.5 text-center text-xs text-fg-muted"><MessageSquareDashed className="size-3.5 shrink-0" /> {t("temporary.on")}</p>}
      {chosen.length > 1 && !compact && <p className="text-center text-xs text-fg-muted">{t("multi.manyHint", { count: chosen.length })}</p>}
      {suggestions && (
        <div className="flex flex-wrap justify-center gap-2">
          {(["research", "write", "design", "analyze"] as const).map((k) => (
            <button key={k} type="button" onClick={() => { setText(t(`space.suggest.${k}.prompt`)); ref.current?.focus(); }} className="rounded-full border border-border px-3 py-1.5 text-sm text-fg-muted hover:border-accent hover:text-fg">{t(`space.suggest.${k}.label`)}</button>
          ))}
        </div>
      )}
    </div>
  );
}

type Group = "pinned" | "today" | "yesterday" | "week" | "month" | "older";

function groupOf(c: Chat, now: Date): Group {
  if (c.pinned) return "pinned";
  const d = new Date(c.updatedAt);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  return diff <= 0 ? "today" : diff === 1 ? "yesterday" : diff < 7 ? "week" : diff < 30 ? "month" : "older";
}

/** The searched words marked in a passage. */
function Highlight({ text, needle }: { text: string; needle: string }) {
  const i = needle ? text.toLowerCase().indexOf(needle) : -1;
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark className="rounded bg-warn-soft px-0.5 text-fg">{text.slice(i, i + needle.length)}</mark>{text.slice(i + needle.length)}</>;
}

const FOLDED_KEY = "codvero.space.folded";
/** Spaces whose conversations are folded away in the sidebar (remembered on this device). */
function useFoldedSpaces(): [Set<string>, (id: string) => void] {
  const [folded, setFolded] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FOLDED_KEY) ?? "[]") as string[]); } catch { return new Set(); }
  });
  const toggle = (id: string) => setFolded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    try { localStorage.setItem(FOLDED_KEY, JSON.stringify([...next])); } catch { /* private mode */ }
    return next;
  });
  return [folded, toggle];
}

/** Sidebar of the Chat section: new chat, search, the conversations of each space together, then the others grouped
 *  by time (like Claude). */
export function SpaceSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toMsg = useApiError();
  const { confirm, prompt, choose } = useConfirm();
  const params = useParams({ strict: false }) as { chatId?: string };
  const space = usePersonalSpace();
  const p = space.data;
  const chats = useQuery({ queryKey: ["chats", p?.id, "list"], queryFn: () => chatsApi.list(p!.id), enabled: !!p, refetchInterval: 10_000 });
  const spaces = useQuery({ queryKey: ["chat-spaces"], queryFn: spacesApi.list, staleTime: 60_000 });
  const [folded, toggleFolded] = useFoldedSpaces();
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [sharing, setSharing] = useState<Chat | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["chats", p?.id, "list"] });
  const update = useMutation({ mutationFn: ({ c, patch }: { c: Chat; patch: Parameters<typeof chatsApi.update>[1] }) => chatsApi.update(c.id, patch), onSuccess: refresh });
  const fork = useMutation({ mutationFn: (c: Chat) => chatsApi.fork(c.id), onSuccess: (c) => { refresh(); void nav({ to: "/chat/$chatId", params: { chatId: c.id } }); onNavigate?.(); } });
  const remove = useMutation({ mutationFn: (c: Chat) => chatsApi.remove(c.id), onSuccess: (_d, c) => { refresh(); if (params.chatId === c.id) void nav({ to: "/chat" }); } });
  const err = update.error ?? fork.error ?? remove.error;

  const rtf = useMemo(() => new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto", style: "narrow" }), [i18n.language]);
  const rel = (iso: string) => {
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return rtf.format(0, "second");
    if (s < 3600) return rtf.format(-Math.floor(s / 60), "minute");
    if (s < 86400) return rtf.format(-Math.floor(s / 3600), "hour");
    return rtf.format(-Math.floor(s / 86400), "day");
  };
  const needle = q.trim().toLowerCase();
  // the messages themselves are searched too (after a short pause in typing)
  const [deep, setDeep] = useState("");
  useEffect(() => { const h = setTimeout(() => setDeep(needle), 350); return () => clearTimeout(h); }, [needle]);
  const found = useQuery({ queryKey: ["chats", p?.id, "search", deep], queryFn: () => chatsApi.search(p!.id, deep), enabled: !!p && deep.length >= 2, staleTime: 15_000 });
  const all = (chats.data ?? []).filter((c) => !needle || c.title.toLowerCase().includes(needle));
  const active = all.filter((c) => !c.archived).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const archived = all.filter((c) => c.archived);
  const now = new Date();
  // pinned chats stay on top; the rest of a space's chats sit together under it (spaces with the latest activity
  // first); chats outside any space are grouped by time
  const spaceById = new Map((spaces.data ?? []).map((s) => [s.id, s]));
  // a space taken off the sidebar: its conversations are listed by date with the others
  const inSpace = (c: Chat) => !c.pinned && !!c.spaceId && !!spaceById.get(c.spaceId) && !spaceById.get(c.spaceId)!.hidden;
  // pinned spaces first (even without conversations yet, unless a search leaves them empty), then the others by
  // their latest activity
  const spaceGroups = (spaces.data ?? [])
    .map((s) => ({ s, items: active.filter((c) => inSpace(c) && c.spaceId === s.id) }))
    .filter((x) => !x.s.hidden && (x.items.length || (x.s.pinned && !needle)))
    .sort((a, b) => Number(b.s.pinned) - Number(a.s.pinned) || (b.items[0]?.updatedAt ?? "").localeCompare(a.items[0]?.updatedAt ?? ""));
  const refreshSpaces = () => void qc.invalidateQueries({ queryKey: ["chat-spaces"] });
  const pinSpace = useMutation({ mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => spacesApi.pin(id, pinned), onSuccess: refreshSpaces });
  const hideSpace = useMutation({ mutationFn: (id: string) => spacesApi.setHidden(id, true), onSuccess: refreshSpaces });
  const removeSpace = useMutation({ mutationFn: (id: string) => spacesApi.remove(id), onSuccess: () => { refreshSpaces(); refresh(); } });
  // delete: the whole space, or only its place in this sidebar
  const askRemoveSpace = async (s: ChatSpace) => {
    const how = await choose({ title: t("spaces.deleteTitle"), message: t("spaces.deleteChoice", { name: s.name }), confirmLabel: t("spaces.deleteWhole"), altLabel: t("spaces.hideFromSidebar"), danger: true });
    if (how === "confirm") removeSpace.mutate(s.id);
    else if (how === "alt") hideSpace.mutate(s.id);
  };
  const rest = active.filter((c) => !inSpace(c));
  const groups = (["pinned", "today", "yesterday", "week", "month", "older"] as Group[]).map((g) => ({ g, items: rest.filter((c) => groupOf(c, now) === g) })).filter((x) => x.items.length);
  const row = (c: Chat) => (
    <ChatRow
      key={c.id}
      c={c}
      p={p!}
      activeChatId={params.chatId ?? null}
      rel={rel}
      onNavigate={onNavigate}
      onDeleteChat={async (x) => { if (await confirm({ title: t("chatMenu.deleteTitle"), message: t("chatMenu.deleteMsg", { title: x.title }), confirmLabel: t("chatMenu.delete"), cancelLabel: t("chatMenu.keep"), danger: true })) remove.mutate(x); }}
      onForkChat={(x) => fork.mutate(x)}
      onRenameChat={async (x) => { const v = await prompt({ title: t("chatMenu.rename"), input: { initial: x.title, label: t("chat.rename") }, confirmLabel: t("common.save") }); if (v && v.trim() !== x.title) update.mutate({ c: x, patch: { title: v.trim() } }); }}
      onPatchChat={(x, patch) => update.mutate({ c: x, patch })}
      onShareMemory={(x) => setSharing(x)}
    />
  );

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-2 p-3">
        <div className="flex gap-2">
          <Button className="min-w-0 flex-1 justify-start" variant="secondary" onClick={() => { void nav({ to: "/chat" }); onNavigate?.(); }}><SquarePen className="size-4" /> {t("space.newChat")}</Button>
          <Link to="/chat/temporary" onClick={onNavigate} aria-label={t("temporary.new")} title={t("temporary.new")} className="grid size-9 shrink-0 place-items-center rounded-md border border-border text-fg-muted hover:bg-surface-2 hover:text-fg" activeProps={{ className: "border-accent bg-accent-soft text-fg" }}><MessageSquareDashed className="size-4" /></Link>
        </div>
        {err && <button type="button" className="w-full rounded-md border border-danger/40 bg-danger-soft px-2 py-1.5 text-start text-xs text-danger" onClick={() => { update.reset(); fork.reset(); remove.reset(); }}>{toMsg(err)} ✕</button>}
        <div className="relative">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-fg-muted" />
          <Input data-sidebar-search className="h-8 ps-8 text-xs" placeholder={t("space.search")} value={q} onChange={(e) => setQ(e.target.value)} />
          {q && <button type="button" aria-label={t("common.close")} className="absolute end-2 top-1/2 -translate-y-1/2 text-fg-muted" onClick={() => setQ("")}><X className="size-3.5" /></button>}
        </div>
      </div>
      <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {!p || chats.isLoading ? <div className="grid place-items-center py-6"><Spinner /></div> : null}
        {p && chats.data && groups.length === 0 && spaceGroups.length === 0 && !(needle.length >= 2 && found.data?.length) && <p className="px-2 py-6 text-center text-xs text-fg-muted">{needle ? t("space.noResults") : t("space.empty")}</p>}
        {groups.filter((x) => x.g === "pinned").map(({ g, items }) => (
          <section key={g} className="mb-3">
            <h3 className="px-2 pb-1 text-[11px] font-medium text-fg-muted">{t(`space.groups.${g}`)}</h3>
            <ul>{items.map(row)}</ul>
          </section>
        ))}
        {spaceGroups.map(({ s, items }) => {
          // a search opens every space; a folded one still shows the chat that is open
          const closed = folded.has(s.id) && !needle;
          const shown = closed ? items.filter((c) => c.id === params.chatId) : items;
          return (
            <section key={s.id} className="mb-2">
              <div className="group/space flex items-center gap-1 rounded-md pe-1 hover:bg-surface-2">
                <button type="button" aria-expanded={!closed} onClick={() => toggleFolded(s.id)} className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-start">
                  <SpaceBadge space={s} size="xs" />
                  <span dir="auto" className="min-w-0 flex-1 truncate text-xs font-semibold">{s.name}</span>
                  <span className="shrink-0 text-[11px] text-fg-muted">{items.length}</span>
                  <ChevronDown className={cn("size-3.5 shrink-0 text-fg-muted transition-transform", closed && "-rotate-90 rtl:rotate-90")} />
                </button>
                {/* the space's menu: always there (no hover on a touch screen) */}
                <Menu
                  trigger={<button type="button" aria-label={t("spaces.menu", { name: s.name })} className="tap grid shrink-0 place-items-center rounded-md border border-transparent px-1.5 py-0.5 text-fg-muted hover:border-border hover:bg-surface-2 hover:text-fg"><MoreHorizontal className="size-3.5" /></button>}
                  items={[
                    { label: t("spaces.goTo"), icon: <FolderOpen className="size-4" />, onSelect: () => { void nav({ to: "/chat/spaces/$spaceId", params: { spaceId: s.id } }); onNavigate?.(); } },
                    { label: s.pinned ? t("spaces.unpin") : t("spaces.pin"), icon: s.pinned ? <PinOff className="size-4" /> : <Pin className="size-4" />, onSelect: () => pinSpace.mutate({ id: s.id, pinned: !s.pinned }) },
                    { label: t("common.delete"), icon: <Trash2 className="size-4" />, danger: true, divider: true, onSelect: () => void askRemoveSpace(s) },
                  ]}
                />
              </div>
              {shown.length > 0 && <ul className="ms-3 border-s border-border ps-1">{shown.map(row)}</ul>}
            </section>
          );
        })}
        {groups.filter((x) => x.g !== "pinned").map(({ g, items }) => (
          <section key={g} className="mb-3">
            <h3 className="px-2 pb-1 text-[11px] font-medium text-fg-muted">{t(`space.groups.${g}`)}</h3>
            <ul>{items.map(row)}</ul>
          </section>
        ))}
        {needle.length >= 2 && (found.data ?? []).filter((r) => !all.some((c) => c.id === r.chatId)).length > 0 && (
          <section className="mb-3">
            <h3 className="px-2 pb-1 text-[11px] font-medium text-fg-muted">{t("space.inMessages")}</h3>
            <ul>
              {(found.data ?? []).filter((r) => !all.some((c) => c.id === r.chatId)).map((r) => (
                <li key={r.chatId}>
                  <Link to="/chat/$chatId" params={{ chatId: r.chatId }} onClick={onNavigate} className="block rounded-md px-2 py-1.5 hover:bg-surface-2" activeProps={{ className: "bg-accent-soft" }}>
                    <span dir="auto" className="block truncate text-sm">{r.title}</span>
                    <span dir="auto" className="line-clamp-2 text-[11px] text-fg-muted"><Highlight text={r.snippet} needle={deep} /></span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
        {archived.length > 0 && (
          <section className="mb-3">
            <button type="button" className="px-2 pb-1 text-[11px] font-medium text-fg-muted hover:text-fg" onClick={() => setShowArchived(!showArchived)}>{t("chatMenu.archived", { count: archived.length })}</button>
            {showArchived && <ul>{archived.map(row)}</ul>}
          </section>
        )}
      </nav>
      {sharing && (
        <MemoryShareDialog
          chat={sharing}
          others={(chats.data ?? []).filter((c) => c.id !== sharing.id && !c.parentId && c.memoryFrom !== sharing.id)}
          onPick={(memoryFrom) => { update.mutate({ c: sharing, patch: { memoryFrom } }); setSharing(null); }}
          onClose={() => setSharing(null)}
        />
      )}
      {/* the section's own tools: skills and MCP connections for its providers */}
      <div className="space-y-0.5 border-t border-border p-2">
        <Link to="/chat/spaces" onClick={onNavigate} className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-fg-muted hover:bg-surface-2 hover:text-fg" activeProps={{ className: "bg-accent-soft text-fg" }}><FolderOpen className="size-4" /> {t("spaces.title")}</Link>
        <Link to="/chat/schedules" onClick={onNavigate} className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-fg-muted hover:bg-surface-2 hover:text-fg" activeProps={{ className: "bg-accent-soft text-fg" }}><CalendarClock className="size-4" /> {t("dock.schedules")}</Link>
        <Link to="/chat/workflows" onClick={onNavigate} className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-fg-muted hover:bg-surface-2 hover:text-fg" activeProps={{ className: "bg-accent-soft text-fg" }}><Workflow className="size-4" /> {t("workflows.title")}</Link>
        <Link to="/chat/skills" onClick={onNavigate} className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-fg-muted hover:bg-surface-2 hover:text-fg" activeProps={{ className: "bg-accent-soft text-fg" }}><Code2 className="size-4" /> {t("chatTools.skillsTitle")}</Link>
        <Link to="/chat/mcp" onClick={onNavigate} className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-fg-muted hover:bg-surface-2 hover:text-fg" activeProps={{ className: "bg-accent-soft text-fg" }}><Plug className="size-4" /> {t("chatTools.mcpTitle")}</Link>
      </div>
    </div>
  );
}
